import {
  IonFab,
  IonHeader,
  IonLoading,
  IonRefresher,
  IonRefresherContent,
  useIonAlert,
  IonContent,
} from "@ionic/react";
import { useCallback, useContext, useEffect, useState } from "react";
import { useParams } from "react-router";
import { decodeParam } from "../util/ApiHelper";
import { RefresherEventDetail } from "@ionic/core";
import { WoDocument } from "../models/workorders/Documents";
import FileUploadFAB from "./FileUploadFAB";
import { useDocumentManager } from "../hooks/useDocumentManager";
import { appInsights, reactPlugin } from "../util/AppInsights";
import {
  TranslationMessagesContext,
  TranslationsContext,
} from "../util/Translations";
import ApiError from "./ApiError";
import OnlineStatus from "./OnlineStatus";
import { getLinkedDocInfoList } from "../api/WorkOrders";
import { useWorkOrderStore } from "../store/wo-order";
import { useShallow } from "zustand/react/shallow";
import { withAITracking } from "@microsoft/applicationinsights-react-js";
import DocumentListDisplay from "../components/DocumentListDisplay";
import "../pages/Page.css";

const DocumentsTab: React.FC = () => {
  const { translations } = useContext(TranslationsContext);
  const { translatedMessages } = useContext(TranslationMessagesContext);
  appInsights?.trackPageView({ name: "DocumentsTab" });
  const { workOrderId } = useParams<{ workOrderId: string }>();
  let displayWo = decodeParam(workOrderId);
  const { viewDocument } = useDocumentManager();

  const [showLoading, setShowLoading] = useState(false);
  const [presentAlert] = useIonAlert();

  const [errorData, setErrorData] = useState("");
  const documents = useWorkOrderStore(
    useShallow((state) => state.getDocumentsForWo(displayWo))
  );
  const setWoDocuments = useWorkOrderStore((state) => state.setWoDocuments);

  const loadWoDocuments = useCallback(
    async (refetch: boolean = false) => {
      if (documents && !refetch) return; //we already have documents in cache

      const response = await getLinkedDocInfoList(displayWo);

      if (response.isError) {
        setErrorData(response.data?.toString());
        setWoDocuments(displayWo, []);
      } else {
        setWoDocuments(displayWo, response.data ?? []);
        setErrorData("");
      }
      setShowLoading(false);
    },
    [displayWo, documents, setWoDocuments]
  );

  const reloadDocs = useCallback(async () => {
    setShowLoading(true);
    await loadWoDocuments(true);
    setShowLoading(false);
  }, [loadWoDocuments]);

  const handleRefresh = async (event: CustomEvent<RefresherEventDetail>) => {
    await reloadDocs();
    event.detail.complete();
  };

  useEffect(() => {
    (async () => {
      setShowLoading(true);
      // Get documents tab data
      await loadWoDocuments();
      setShowLoading(false);
    })();
  }, [loadWoDocuments, workOrderId]);

  const onDocumentClick = async (document: WoDocument) => {
    if (document && !!document.DownloadURL) {
      setShowLoading(true);

      await viewDocument({ WoNumber: displayWo, Document: document });

      setShowLoading(false);
    } else {
      showAlert(
        translatedMessages["ErrDocViewing"]?.MessageText ||
        "This document cannot be viewed on this device"
      );
    }
  };

  const showAlert = (message: string) => {
    presentAlert({
      message: message,
      buttons: ["OK"],
    });
  };

  return (
    <>
      <IonContent fullscreen>
      <IonHeader>
        <OnlineStatus />
      </IonHeader>
      <ApiError errorData={errorData} />
      <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
        <IonRefresherContent></IonRefresherContent>
      </IonRefresher>
      <IonLoading
        isOpen={showLoading}
        onDidDismiss={() => setShowLoading(false)}
        duration={2000}
      />
      <DocumentListDisplay
        documents={documents}
        onDocumentClick={onDocumentClick}
        translations={translations}
        translatedMessages={translatedMessages}
        showLoading={showLoading}
        errorData={errorData}
      />
      </IonContent>
      <IonFab
        vertical="bottom"
        horizontal="end"
        style={{
          position: "fixed",
          bottom: "calc(16px + env(safe-area-inset-bottom))",
          right: "16px",
          zIndex: 1000,
        }}
      >
        <FileUploadFAB
          WoNumber={displayWo}
          cameraEnabled={true}
          fileEnabled={true}
          isSmall={false}
          processCompletedCallback={reloadDocs}
        />
      </IonFab>
    </>
  );
};

export default withAITracking(reactPlugin, DocumentsTab, "DocumentsTab");
